import * as turf from "@turf/turf";
import type { FeatureCollection } from "geojson";
import type mapboxgl from "mapbox-gl";
import type { Expression } from "mapbox-gl";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { flattenToPoints } from "../utils/flattenToPoints";

interface UseHeatMapProps {
  map: mapboxgl.Map | null;
  geoData: FeatureCollection;
  type: "heatmap" | "hexabin";
  cellSize?: number;
  mapbounds: FeatureCollection; // Keep as FeatureCollection to work with turf.bbox
  heatmapStyle: {
    intensity: number;
    style: "heatmap" | "hexabin" | "drawn";
  };
  setLabelsData: (data: any) => void;
  maxDataPoints?: number; // Performance: configurable limit
  enableDataSampling?: boolean; // Performance: data sampling
  emergencyLimit?: number; // Emergency memory protection
}

const defaultCellSize = 1;

// Simplified cache key generation (no mapbounds since they don't change)
function generateCacheKey(
  geoData: FeatureCollection,
  enableDataSampling: boolean,
  maxDataPoints: number,
): string {
  const featuresHash = geoData.features.length;
  return `${featuresHash}-${enableDataSampling}-${maxDataPoints}`;
}

// Performance: Intelligent data sampling with emergency limits
function sampleData(
  geoData: FeatureCollection,
  maxPoints: number,
  emergencyLimit: number,
): FeatureCollection {
  const totalFeatures = geoData.features.length;

  // CRITICAL: Emergency memory protection
  if (totalFeatures > emergencyLimit) {
    console.warn(
      `Dataset too large (${totalFeatures} features), applying emergency sampling to ${emergencyLimit}`,
    );
    const emergencyStep = Math.ceil(totalFeatures / emergencyLimit);
    const emergencyFeatures = geoData.features.filter(
      (_, index) => index % emergencyStep === 0,
    );
    return {
      type: "FeatureCollection",
      features: emergencyFeatures,
    };
  }

  if (totalFeatures <= maxPoints) {
    return geoData;
  }

  const step = Math.ceil(totalFeatures / maxPoints);
  const sampledFeatures = geoData.features.filter(
    (_, index) => index % step === 0,
  );

  return {
    type: "FeatureCollection",
    features: sampledFeatures,
  };
}

// CRITICAL: Estimate memory usage to prevent crashes
function estimateMemoryUsage(geoData: FeatureCollection): number {
  if (!geoData?.features) return 0;

  let estimate = 0;
  for (const feature of geoData.features.slice(0, 100)) {
    // Sample first 100 features
    if (feature.geometry) {
      switch (feature.geometry.type) {
        case "Polygon":
          estimate += (feature.geometry.coordinates[0]?.length || 0) * 8; // 8 bytes per coordinate pair
          break;
        case "LineString":
          estimate += (feature.geometry.coordinates?.length || 0) * 8;
          break;
        case "Point":
          estimate += 16; // 2 coordinates * 8 bytes
          break;
      }
    }
  }

  // Extrapolate to full dataset
  return (
    (estimate * geoData.features.length) /
    Math.min(100, geoData.features.length)
  );
}

function countCoordinatesInFeatureCollection(featureCollection) {
  if (!featureCollection?.features) return 0;

  let coordinateCount = 0;

  // CRITICAL: Limit processing to prevent memory issues
  const maxFeaturesToCount = 10000;
  const featuresToProcess = featureCollection.features.slice(
    0,
    maxFeaturesToCount,
  );

  for (const feature of featuresToProcess) {
    if (feature?.geometry) {
      const geometryType = feature.geometry.type;

      switch (geometryType) {
        case "Point":
        case "MultiPoint":
          coordinateCount += 1;
          break;

        case "LineString":
        case "MultiLineString":
          coordinateCount += feature.geometry.coordinates?.length || 0;
          break;

        case "Polygon":
        case "MultiPolygon":
          if (feature.geometry.coordinates) {
            for (const polygonCoordinates of feature.geometry.coordinates) {
              coordinateCount += polygonCoordinates?.length || 0;
            }
          }
          break;

        default:
          break;
      }
    }
  }

  // Extrapolate if we limited the count
  if (featureCollection.features.length > maxFeaturesToCount) {
    coordinateCount = Math.floor(
      (coordinateCount * featureCollection.features.length) /
        maxFeaturesToCount,
    );
  }

  return coordinateCount;
}

const getHeatMapRadius = (baseSize: number, mapboundsArea: number) => {
  return [
    "interpolate",
    ["exponential", 2],
    ["zoom"],
    0,
    0,
    18,
    baseSize * (Math.cbrt(mapboundsArea) / 20),
  ] as Expression;
};

const getHeatMapIntensity = (intensity: number) => {
  return [
    "interpolate",
    ["exponential", 2],
    ["zoom"],
    0,
    intensity / 125,
    18,
    intensity / 125,
  ] as Expression;
};

const getHeatMapColor = () => {
  return [
    "interpolate",
    ["linear"],
    ["heatmap-density"],
    0,
    "rgba(255,208,97,0)",
    0.1,
    "rgba(255,208,97,0.25)",
    0.2,
    "rgba(255,208,97,0.5)",
    0.3,
    "rgba(255,192,83,0.6)",
    0.4,
    "rgba(255,182,70,0.7)",
    0.5,
    "rgba(255,170,57,0.8)",
    0.6,
    "rgba(255,157,44,0.8)",
    0.7,
    "rgba(247,133,33,0.85)",
    0.8,
    "rgba(239,101,29,0.9)",
    0.9,
    "rgba(208,52,36,0.95)",
    1,
    "rgba(178,24,43,1)",
  ] as Expression;
};

const useHeatMap = ({
  map,
  geoData,
  type,
  cellSize = defaultCellSize,
  mapbounds,
  heatmapStyle,
  setLabelsData,
  maxDataPoints = 50000, // Performance: default limit
  enableDataSampling = true, // Performance: enabled by default
  emergencyLimit = 10000, // CRITICAL: Emergency memory protection
}: UseHeatMapProps) => {
  const prevType = useRef<string | null>(null);
  const sourceId = "heatmap-source";
  const layerId = "heatmap-layer";

  // CRITICAL: Instance-specific caches to prevent memory leaks
  const dataCacheRef = useRef(new Map<string, FeatureCollection>());
  const flattenCacheRef = useRef(new Map<string, FeatureCollection>());
  const processingRef = useRef<{ cancelled: boolean }>({ cancelled: false });

  // CRITICAL: Simplified data processing with memory protection
  const processedGeoData = useMemo(() => {
    if (!geoData?.features?.length) return geoData;

    // Reset cancellation flag
    processingRef.current = { cancelled: false };

    // CRITICAL: Check estimated memory usage
    const estimatedMemory = estimateMemoryUsage(geoData);
    const memoryLimitMB = 100; // 100MB limit
    if (estimatedMemory > memoryLimitMB * 1024 * 1024) {
      console.warn(
        `Dataset too large (${Math.round(estimatedMemory / 1024 / 1024)}MB), applying emergency limits`,
      );
      // Force aggressive sampling for large datasets
      const emergencySampledData = sampleData(
        geoData,
        emergencyLimit / 2,
        emergencyLimit,
      );
      return emergencySampledData;
    }

    // Simplified cache key (no mapbounds)
    const cacheKey = generateCacheKey(
      geoData,
      enableDataSampling,
      maxDataPoints,
    );

    // Check instance cache first
    if (dataCacheRef.current.has(cacheKey)) {
      return dataCacheRef.current.get(cacheKey)!;
    }

    let processed = geoData;

    try {
      // Sample data if it's too large
      if (
        enableDataSampling &&
        processed.features.length > maxDataPoints &&
        !processingRef.current.cancelled
      ) {
        processed = sampleData(processed, maxDataPoints, emergencyLimit);
      }

      // CRITICAL: Final emergency check
      if (processed.features.length > emergencyLimit) {
        console.warn(
          `Final dataset still too large (${processed.features.length}), applying final emergency sampling`,
        );
        processed = sampleData(processed, emergencyLimit, emergencyLimit);
      }

      // Cache the result with size limit
      dataCacheRef.current.set(cacheKey, processed);

      // CRITICAL: Aggressive cache size management
      if (dataCacheRef.current.size > 10) {
        // Reduced from 50 to 10
        const keysToDelete = Array.from(dataCacheRef.current.keys()).slice(
          0,
          5,
        );
        keysToDelete.forEach((key) => dataCacheRef.current.delete(key));
      }

      return processed;
    } catch (error) {
      console.error("Error processing geo data:", error);
      // Return heavily sampled data as fallback
      return sampleData(geoData, emergencyLimit / 4, emergencyLimit / 4);
    }
  }, [geoData, enableDataSampling, maxDataPoints, emergencyLimit]);

  // CRITICAL: Safe flattened data processing with memory protection
  const flatGeoData = useMemo(() => {
    if (processingRef.current.cancelled) {
      return { type: "FeatureCollection" as const, features: [] };
    }

    try {
      // Simplified flatten cache key (no complex mapbounds)
      const flattenKey = `${processedGeoData.features.length}`;

      if (flattenCacheRef.current.has(flattenKey)) {
        return flattenCacheRef.current.get(flattenKey)!;
      }

      // CRITICAL: Additional safety check before flattening
      if (processedGeoData.features.length > emergencyLimit) {
        console.warn(
          "Dataset too large for flattening, using emergency sample",
        );
        const emergencyData = sampleData(
          processedGeoData,
          emergencyLimit / 2,
          emergencyLimit / 2,
        );
        const result = flattenToPoints(
          mapbounds,
          emergencyData,
          emergencyLimit / 2,
        );
        flattenCacheRef.current.set(flattenKey, result);
        return result;
      }

      const result = flattenToPoints(
        mapbounds,
        processedGeoData,
        Math.min(maxDataPoints, emergencyLimit),
      );

      // Cache with aggressive size management
      flattenCacheRef.current.set(flattenKey, result);

      if (flattenCacheRef.current.size > 5) {
        // Reduced from 20 to 5
        const keysToDelete = Array.from(flattenCacheRef.current.keys()).slice(
          0,
          2,
        );
        keysToDelete.forEach((key) => flattenCacheRef.current.delete(key));
      }

      return result;
    } catch (error) {
      console.error("Error flattening geo data:", error);
      // Return empty collection as safe fallback
      return { type: "FeatureCollection" as const, features: [] };
    }
  }, [processedGeoData, mapbounds, maxDataPoints, emergencyLimit]);

  const mapboundsArea = useMemo(() => {
    try {
      const mapboundsBbox = turf.bbox(mapbounds);
      const mapboundsBboxPolygon = turf.bboxPolygon(mapboundsBbox);
      return turf.area(mapboundsBboxPolygon);
    } catch (error) {
      console.error("Error calculating mapbounds area:", error);
      return 1000000; // Fallback area
    }
  }, [mapbounds]);

  const coordCount = useMemo(() => {
    try {
      return countCoordinatesInFeatureCollection(flatGeoData);
    } catch (error) {
      console.error("Error counting coordinates:", error);
      return 1000; // Fallback count
    }
  }, [flatGeoData]);

  const factor = useMemo(() => {
    const p = Math.log(15) / Math.log(4);
    return 0.25 + 0.75 * (Math.max(coordCount, 5000) / 5000) ** -p;
  }, [coordCount]);

  const hasAreaPoints = useMemo(() => {
    try {
      return flatGeoData.features.some(
        (feature) => feature.properties?._fromPolygon === true,
      );
    } catch (error) {
      return false;
    }
  }, [flatGeoData]);

  const hasPathPoints = useMemo(() => {
    try {
      return flatGeoData.features.some(
        (feature) => feature.properties?._fromLine === true,
      );
    } catch (error) {
      return false;
    }
  }, [flatGeoData]);

  const updateHeatmap = useCallback(() => {
    if (
      !map ||
      (!(map as mapboxgl.Map & { _loaded: boolean })?._loaded && geoData)
    ) {
      return;
    }

    if (heatmapStyle.style === "drawn") {
      setLabelsData(geoData);
      return;
    }

    try {
      const sourceExists = map.getSource(sourceId);
      const layerExists = map.getLayer(layerId);

      if (type !== prevType.current) {
        if (layerExists) map.removeLayer(layerId);
        if (sourceExists) map.removeSource(sourceId);
        prevType.current = type;
      }

      if (type === "heatmap") {
        let intensityMultiplier = 1;

        if (hasAreaPoints) {
          intensityMultiplier = 0.25;
        } else if (hasPathPoints) {
          intensityMultiplier = 0.5;
        }

        const finalIntensity = heatmapStyle?.intensity * factor;
        const finalRadius = Number(heatmapStyle?.intensity || 0.1);

        if (!sourceExists) {
          map.addSource(sourceId, {
            type: "geojson",
            data: flatGeoData,
          });
        } else {
          (map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(
            flatGeoData,
          );
        }

        if (!layerExists) {
          map.addLayer({
            id: layerId,
            type: "heatmap",
            source: sourceId,
            paint: {
              "heatmap-opacity": 0.7,
              "heatmap-radius": getHeatMapRadius(finalRadius, mapboundsArea),
              "heatmap-intensity": getHeatMapIntensity(
                finalIntensity * intensityMultiplier,
              ),
              "heatmap-color": getHeatMapColor(),
            },
          });
        } else {
          map.setPaintProperty(
            layerId,
            "heatmap-radius",
            getHeatMapRadius(finalRadius, mapboundsArea),
          );
          map.setPaintProperty(
            layerId,
            "heatmap-intensity",
            getHeatMapIntensity(finalIntensity * intensityMultiplier),
          );
        }
      }
    } catch (error) {
      console.error("Error updating heatmap:", error);
    }
  }, [
    geoData,
    flatGeoData,
    hasAreaPoints,
    hasPathPoints,
    heatmapStyle,
    map,
    setLabelsData,
    type,
    factor,
    mapboundsArea,
  ]);

  useEffect(() => {
    updateHeatmap();
  }, [updateHeatmap]);

  // CRITICAL: Comprehensive cleanup and cancellation
  useEffect(() => {
    return () => {
      // Cancel any ongoing processing
      processingRef.current.cancelled = true;

      // Clean up map layers and sources
      try {
        if (map?.getLayer(layerId)) map.removeLayer(layerId);
        if (map?.getSource(sourceId)) map.removeSource(sourceId);
      } catch (error) {
        console.error("Error cleaning up map layers:", error);
      }

      // Clear instance-specific caches
      dataCacheRef.current.clear();
      flattenCacheRef.current.clear();
    };
  }, [map]);
};

export default useHeatMap;
